import React, { useMemo } from 'react';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { scaleLinear } from 'd3-scale';
import { MapChartConfig, MapChartData } from './config/mapChartTypes';

const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

interface SimpleMapViewProps {
    data: MapChartData;
    config: MapChartConfig;
    onPointClick?: (d: any) => void;
    onRegionClick?: (d: any) => void;
}

export const SimpleMapView: React.FC<SimpleMapViewProps> = ({
    data,
    config,
    onPointClick,
    onRegionClick
}) => {
    // Color scale for bubbles/points
    const colorScale = useMemo(() => {
        if (!data.points || data.points.length === 0) return null;
        const values = data.points.map(p => p.value);
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        return scaleLinear()
            .domain([minVal, maxVal])
            .range(['#FCD34D', '#F59E0B'] as any);
    }, [data.points]);

    // Size scale for bubbles
    const sizeScale = useMemo(() => {
        if (!data.points || data.points.length === 0) return null;
        const values = data.points.map(p => p.value);
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        return scaleLinear()
            .domain([minVal, maxVal])
            .range([config.minBubbleRadius || 3, config.maxBubbleRadius || 20]);
    }, [data.points, config.minBubbleRadius, config.maxBubbleRadius]);

    return (
        <div className="w-full h-full relative bg-slate-950 rounded-xl overflow-hidden">
            <ComposableMap
                projection="geoMercator"
                projectionConfig={{
                    scale: 147
                }}
                style={{ width: '100%', height: '100%' }}
            >
                <ZoomableGroup>
                    {/* World Map Geographies */}
                    <Geographies geography={geoUrl}>
                        {({ geographies }) =>
                            geographies.map(geo => (
                                <Geography
                                    key={geo.rsmKey}
                                    geography={geo}
                                    fill="#1e293b"
                                    stroke="#334155"
                                    strokeWidth={0.5}
                                    style={{
                                        default: { outline: 'none' },
                                        hover: { fill: '#334155', outline: 'none' },
                                        pressed: { fill: '#475569', outline: 'none' }
                                    }}
                                    onClick={() => {
                                        if (onRegionClick) {
                                            onRegionClick({
                                                name: geo.properties.name,
                                                id: geo.id
                                            });
                                        }
                                    }}
                                />
                            ))
                        }
                    </Geographies>

                    {/* Data Points/Bubbles */}
                    {data.points && data.points.map((point, idx) => {
                        const size = sizeScale ? sizeScale(point.value) : 5;
                        const color = colorScale ? colorScale(point.value) : '#F59E0B';

                        return (
                            <Marker
                                key={idx}
                                coordinates={[point.lon, point.lat]}
                                onClick={() => onPointClick && onPointClick(point)}
                                style={{ cursor: 'pointer' }}
                            >
                                <circle
                                    r={size}
                                    fill={color}
                                    fillOpacity={config.bubbleOpacity || 0.7}
                                    stroke="#fff"
                                    strokeWidth={0.5}
                                />
                                <title>{`${point.label}: ${point.value}`}</title>
                            </Marker>
                        );
                    })}
                </ZoomableGroup>
            </ComposableMap>

            {/* Info overlay */}
            <div className="absolute bottom-4 left-4 bg-black/60 text-white text-xs px-2 py-1 rounded">
                {data.points?.length || 0} locations
            </div>
        </div>
    );
};
